/* World map of successful logins grouped by city (OpenStreetMap tiles). */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { API, getAuthHeader } from "@/App";
import { RefreshCw } from "lucide-react";
import { CountryReport } from "@/components/CountryReport";

const radius = (n, max) => 6 + 18 * Math.sqrt(n / Math.max(1, max));

export const LoginMapPanel = () => {
  const [days, setDays] = useState(7);
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/security/login-map`, { headers: getAuthHeader(), params: { days } });
      setD(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load login map"); }
  }, [days]);
  useEffect(() => { load(); }, [load]);
  const max = Math.max(1, ...(d?.cities || []).map(c => c.count));
  return (
    <div className="space-y-3" data-testid="login-map-panel">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {[7, 30].map(n => (
          <button key={n} type="button" onClick={() => setDays(n)} data-testid={`login-map-days-${n}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${days === n ? "bg-purple-600 text-white border-purple-600" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}>
            Last {n} days
          </button>
        ))}
        <button type="button" onClick={load} className="rounded-full border border-slate-300 p-1.5 text-slate-600 hover:bg-slate-50" data-testid="login-map-refresh" aria-label="Refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        {d && (
          <span className="text-xs text-slate-500" data-testid="login-map-summary">
            <b className="text-slate-800">{d.total}</b> logins · <b className="text-slate-800">{d.cities.length}</b> cities · <b className="text-slate-800">{d.countries}</b> countries
            {d.unknown > 0 && <> · {d.unknown} without location (office network / not yet located)</>}
          </span>
        )}
      </div>
      <div className="grid gap-3 lg:grid-cols-[1fr_300px]">
        <div className="h-[520px] overflow-hidden rounded-lg border" data-testid="login-map">
          <MapContainer center={[22, 40]} zoom={2} minZoom={2} worldCopyJump style={{ height: "100%", width: "100%" }}>
            <TileLayer attribution='&copy; OpenStreetMap contributors' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
            {(d?.cities || []).map(c => (
              <CircleMarker key={`${c.country_code}-${c.city}`} center={[c.lat, c.lon]} radius={radius(c.count, max)}
                pathOptions={{ color: "#7c3aed", fillColor: "#8b5cf6", fillOpacity: 0.55, weight: 1.5 }}>
                <Tooltip>{c.city}, {c.country}: {c.count}</Tooltip>
                <Popup>
                  <div className="text-xs">
                    <b>{c.city}{c.region ? `, ${c.region}` : ""}, {c.country}</b> — {c.count} login{c.count === 1 ? "" : "s"}
                    <ul className="mt-1">{c.users.map(u => <li key={u.email}>{u.name} <span className="text-slate-400">({u.count})</span></li>)}</ul>
                  </div>
                </Popup>
              </CircleMarker>
            ))}
          </MapContainer>
        </div>
        <div className="max-h-[520px] overflow-auto rounded-lg border bg-white" data-testid="login-map-list">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600 sticky top-0"><tr><th className="p-2 text-left">City</th><th className="p-2 text-right">Logins</th></tr></thead>
            <tbody>
              {(d?.cities || []).map((c, i) => (
                <tr key={`${c.country_code}-${c.city}`} className="border-t align-top" data-testid={`login-map-city-${i}`}>
                  <td className="p-2">
                    <span className="inline-flex items-center gap-1 font-medium text-slate-800">
                      <img src={`https://flagcdn.com/16x12/${c.country_code}.png`} alt="" width={16} height={12} /> {c.city}, {c.country}
                    </span>
                    <div className="text-[10px] text-slate-500">{c.users.map(u => `${u.name} (${u.count})`).join(", ")}</div>
                  </td>
                  <td className="p-2 text-right font-bold text-purple-700">{c.count}</td>
                </tr>
              ))}
              {d && !d.cities.length && <tr><td colSpan={2} className="p-6 text-center text-slate-400">No located logins in this period</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <CountryReport />
    </div>
  );
};
